import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useCallback,
} from "react";
import roomFileService from "../services/rooms/roomFileService";
import roomResourceService from "../services/rooms/roomResourceService";
import roomSocketService from "../services/rooms/roomSocketService";

const RoomFilesContext = createContext();

export const useRoomFiles = () => {
  const context = useContext(RoomFilesContext);
  if (!context) {
    throw new Error("useRoomFiles must be used within a RoomFilesProvider");
  }
  return context;
};

export const RoomFilesProvider = ({ children }) => {
  // File state
  const [files, setFiles] = useState([]);
  const [filesLoading, setFilesLoading] = useState(false);
  const [filesError, setFilesError] = useState(null);

  // Resource state
  const [resources, setResources] = useState([]);
  const [resourcesLoading, setResourcesLoading] = useState(false);
  const [resourcesError, setResourcesError] = useState(null);

  // Folders
  const [folders, setFolders] = useState([]);

  // Current room
  const [currentRoomId, setCurrentRoomId] = useState(null);

  /**
   * Fetch files for a room
   */
  const fetchFiles = useCallback(async (roomId, filters = {}) => {
    if (!roomId) return;

    setFilesLoading(true);
    setFilesError(null);

    try {
      const response = await roomFileService.getAttachments(roomId, filters);

      if (response.success) {
        setFiles(response.data);
        return response.data;
      } else {
        setFilesError(response.message || "Failed to fetch files");
        return [];
      }
    } catch (error) {
setFilesError(error.message || "Failed to fetch files");
      return [];
    } finally {
      setFilesLoading(false);
    }
  }, []);

  /**
   * Upload file
   */
  const uploadFile = useCallback(
    async (roomId, file, options = {}, onProgress) => {
      try {
        const response = await roomFileService.uploadFile(
          roomId,
          file,
          options,
          onProgress
        );

        if (response.success) {
          // Add to files list
          setFiles((prev) => [response.data, ...prev]);

          // Notify via socket
          roomSocketService.notifyFileUploaded(roomId, response.data);

          return response;
        } else {
          throw new Error(response.message || "Upload failed");
        }
      } catch (error) {
throw error;
      }
    },
    []
  );

  /**
   * Download file
   */
  const downloadFile = useCallback(async (roomId, attachmentId) => {
    try {
      return await roomFileService.downloadFile(roomId, attachmentId);
    } catch (error) {
throw error;
    }
  }, []);

  /**
   * Delete file
   */
  const deleteFile = useCallback(async (roomId, attachmentId) => {
    try {
      const response = await roomFileService.deleteAttachment(
        roomId,
        attachmentId
      );

      if (response.success) {
        // Remove from files list
        setFiles((prev) => prev.filter((f) => f._id !== attachmentId));
        return response;
      } else {
        throw new Error(response.message || "Delete failed");
      }
    } catch (error) {
throw error;
    }
  }, []);

  /**
   * Search files
   */
  const searchFiles = useCallback(async (roomId, query) => {
    try {
      const response = await roomFileService.searchFiles(roomId, query);
      return response.success ? response.data : [];
    } catch (error) {
return [];
    }
  }, []);

  /**
   * Get file versions
   */
  const getFileVersions = useCallback(async (roomId, attachmentId) => {
    try {
      const response = await roomFileService.getFileVersions(
        roomId,
        attachmentId
      );
      return response.success ? response.data : [];
    } catch (error) {
return [];
    }
  }, []);

  /**
   * Fetch resources for a room
   */
  const fetchResources = useCallback(async (roomId, filters = {}) => {
    if (!roomId) return;

    setResourcesLoading(true);
    setResourcesError(null);

    try {
      const response = await roomResourceService.getResources(roomId, filters);

      if (response.success) {
        setResources(response.data);
        return response.data;
      } else {
        setResourcesError(response.message || "Failed to fetch resources");
        return [];
      }
    } catch (error) {
setResourcesError(error.message || "Failed to fetch resources");
      return [];
    } finally {
      setResourcesLoading(false);
    }
  }, []);

  /**
   * Fetch folders
   */
  const fetchFolders = useCallback(async (roomId) => {
    if (!roomId) return;

    try {
      const response = await roomResourceService.getFolders(roomId);

      if (response.success) {
        setFolders(response.data);
        return response.data;
      }
      return [];
    } catch (error) {
return [];
    }
  }, []);

  /**
   * Create resource
   */
  const createResource = useCallback(async (roomId, resourceData) => {
    try {
      const response = await roomResourceService.createResource(
        roomId,
        resourceData
      );

      if (response.success) {
        // Add to resources list
        setResources((prev) => [response.data, ...prev]);

        // Notify via socket
        roomSocketService.notifyResourceCreated(roomId, response.data);

        return response;
      } else {
        throw new Error(response.message || "Failed to create resource");
      }
    } catch (error) {
throw error;
    }
  }, []);

  /**
   * Update resource
   */
  const updateResource = useCallback(async (roomId, resourceId, updates) => {
    try {
      const response = await roomResourceService.updateResource(
        roomId,
        resourceId,
        updates
      );

      if (response.success) {
        // Update in resources list
        setResources((prev) =>
          prev.map((r) => (r._id === resourceId ? response.data : r))
        );
        return response;
      } else {
        throw new Error(response.message || "Failed to update resource");
      }
    } catch (error) {
throw error;
    }
  }, []);

  /**
   * Delete resource
   */
  const deleteResource = useCallback(async (roomId, resourceId) => {
    try {
      const response = await roomResourceService.deleteResource(
        roomId,
        resourceId
      );

      if (response.success) {
        // Remove from resources list
        setResources((prev) => prev.filter((r) => r._id !== resourceId));
        return response;
      } else {
        throw new Error(response.message || "Failed to delete resource");
      }
    } catch (error) {
throw error;
    }
  }, []);

  /**
   * Toggle pin resource
   */
  const togglePinResource = useCallback(
    async (roomId, resourceId, isPinned) => {
      try {
        return await updateResource(roomId, resourceId, { isPinned });
      } catch (error) {
throw error;
      }
    },
    [updateResource]
  );

  /**
   * Search resources
   */
  const searchResources = useCallback(async (roomId, query) => {
    try {
      const response = await roomResourceService.searchResources(roomId, query);
      return response.success ? response.data : [];
    } catch (error) {
return [];
    }
  }, []);

  /**
   * Setup socket listeners for real-time updates
   */
  useEffect(() => {
    if (!currentRoomId) return;

    // File events
    const unsubscribeFileUploaded = roomSocketService.on(
      "new_file_uploaded",
      (data) => {
if (data.attachment) {
          setFiles((prev) => {
            // Avoid duplicates
            if (prev.some((f) => f._id === data.attachment._id)) {
              return prev;
            }
            return [data.attachment, ...prev];
          });
        }
      }
    );

    const unsubscribeFileDeleted = roomSocketService.on(
      "file_deleted",
      (data) => {
if (data.attachmentId) {
          setFiles((prev) => prev.filter((f) => f._id !== data.attachmentId));
        }
      }
    );

    // Resource events
    const unsubscribeResourceCreated = roomSocketService.on(
      "resource_created",
      (data) => {
if (data.resource) {
          setResources((prev) => {
            // Avoid duplicates
            if (prev.some((r) => r._id === data.resource._id)) {
              return prev;
            }
            return [data.resource, ...prev];
          });
        }
      }
    );

    const unsubscribeResourceUpdated = roomSocketService.on(
      "resource_updated",
      (data) => {
if (data.resource) {
          setResources((prev) =>
            prev.map((r) => (r._id === data.resource._id ? data.resource : r))
          );
        }
      }
    );

    const unsubscribeResourceDeleted = roomSocketService.on(
      "resource_deleted",
      (data) => {
if (data.resourceId) {
          setResources((prev) => prev.filter((r) => r._id !== data.resourceId));
        }
      }
    );

    // Cleanup
    return () => {
      unsubscribeFileUploaded();
      unsubscribeFileDeleted();
      unsubscribeResourceCreated();
      unsubscribeResourceUpdated();
      unsubscribeResourceDeleted();
    };
  }, [currentRoomId]);

  const value = {
    // Files
    files,
    filesLoading,
    filesError,
    fetchFiles,
    uploadFile,
    downloadFile,
    deleteFile,
    searchFiles,
    getFileVersions,
    setFiles,

    // Resources
    resources,
    resourcesLoading,
    resourcesError,
    fetchResources,
    createResource,
    updateResource,
    deleteResource,
    togglePinResource,
    searchResources,
    setResources,

    // Folders
    folders,
    fetchFolders,

    // Current room
    currentRoomId,
    setCurrentRoomId,
  };

  return (
    <RoomFilesContext.Provider value={value}>
      {children}
    </RoomFilesContext.Provider>
  );
};

export default RoomFilesContext;
